import { useContext, useEffect, useState } from 'react';
import { getDimensions } from './useGoogleAnalytics';
import getConfig from 'next/config';
import AccountContext from '../../services/AccountContext';
import { useRouter } from 'next/router';

// TODO: store in config somewhere?
const key = 'GTM-P22CW8';

// Only run GTM when in production
const {
  publicRuntimeConfig: { APP_ENV },
} = getConfig();
const shouldLoad = APP_ENV === 'prod' || APP_ENV === 'qa';

// Only load the snippet once per page load
let isLoaded = false;
const load = () => {
  if (isLoaded) {
    return;
  }
  isLoaded = true;

  // Only load real tracking script on prod
  if (shouldLoad) {
    // Load the script asynchronously
    (function(w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', key);
  }
};

const useGTM = (
  grades = [],
  subjects = [],
  contentType = null,
  pageType = null,
  isInteractive = null
) => {
  const { ready, memberId, memberType, isPremium } = useContext(AccountContext);

  // pageTracked keeps track of the path of the last visited page so we can detect pageviews
  const [pageTracked, setPageTracked] = useState(null);

  const router = useRouter();

  useEffect(() => {
    // Wait until auth is ready and route path has changed.
    if (!ready) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    const dimensions = getDimensions(
      memberId,
      memberType,
      isPremium,
      grades,
      subjects,
      contentType,
      pageType,
      isInteractive
    );

    // If this page is the first NextJS page the user is visiting and GTM isn't loaded
    if (!isLoaded) {
      // Push the GTM dimensions but do not add the "page.routeChange" event since these are already getting added to
      // the GTM "Window Loaded" event in the load() function.
      window.dataLayer.push({ ...dimensions });
      setPageTracked(router.asPath);
      load();
      return;
    }

    // If browsing from one NextJS page to another, use "page.routeChange" since there'll be no "Window Loaded" event
    if (pageTracked !== router.asPath) {
      setPageTracked(router.asPath);

      // document.title was not updating consistently because of a NextJS issue. Accessing document.title inside a
      // timeout in routeChangeComplete is a workaround. For more info: https://github.com/vercel/next.js/issues/6025
      // This issue should already be resolved in next@9.4.3
      setTimeout(() => {
        // Push pageview and custom dimensions to GTM
        window.dataLayer.push({
          event: 'page.routeChange',
          'page.routeChange': { page: router.asPath, title: document.title },
          ...dimensions,
        });
      }, 1000);
    }
  }, [ready, router.asPath]);
};

export default useGTM;
